<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import FormDependenciesList from '@Alternatives/Forms/Components/FormDependenciesList.vue'

    import type { FormDefDependenciesReadModel } from '@Alternatives/Forms/Domain/readmodels'
    import type { FormDeleterDialogViewModel } from '@Alternatives/Forms/Components/FormDeleterDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: FormDeleterDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'delete-form', formId: number): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const initialized = computed(() => vm.value.initialized)
    const name = computed(() => vm.value.name)
    const deps = computed<FormDefDependenciesReadModel | null>(() => vm.value.deps)

    const hasDependants = computed<boolean | null>(() => deps.value?.HasDependants ?? null)
    const dependantsCount = computed<number | null>(() => deps.value?.Count ?? null)

    const canDelete = computed(() => initialized.value && hasDependants.value === false)

    // ---------------- actions ----------------

    function handleDelete()
    {
        emit('delete-form', vm.value.id)
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header" :class="{'bg-danger': canDelete}">
                        <h5 class="modal-title">Delete form</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <h4 v-if="!initialized">
                            <i class="fa fa-spinner fa-spin"></i> Checking dependencies...
                        </h4>
                        <div v-if="initialized">
                            <div v-if="canDelete">
                                <h4>Are you sure you want to delete this form?</h4>
                                <p>You are about to delete '<strong>{{name}}</strong>'. This can't be undone.</p>
                            </div>
                            <div v-if="!canDelete">
                                <div class="alert alert-danger text-dark" role="alert">
                                    <h5 class="alert-heading">Cannot delete this form</h5>
                                    <p>
                                        The form '<strong>{{name}}</strong>' is used in <strong>{{dependantsCount}}</strong> places.
                                        To delete this form, first remove it from where it is used.
                                    </p>
                                </div>
                                <FormDependenciesList :readModel="deps" />
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button v-if="canDelete" type="button" class="btn btn-danger" @click="handleDelete">Delete</button>
                        <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .modal-header.bg-danger .modal-title,
    .modal-header.bg-danger .close {
        color: white;
    }
</style>
